import { isToolUIPart, type UIMessage } from "ai";
import { Loader2 } from "lucide-react";
import type { LegacyRef, RefObject } from "react";

import { AssistantText } from "./AssistantText";
import { Card, CardContent } from "./card";
import { textFromMessage } from "./chat-message-utils";
import { ToolCallCard } from "./ToolCallCard";

export type HotboxChatThreadProps = {
  messages: UIMessage[];
  error?: Error | null;
  onDismissError?: () => void;
  showTypingRow: boolean;
  scrollAnchorRef: RefObject<HTMLDivElement | null>;
  emptyPlaceholder?: string;
  /** e.g. https://app.thehotboxsauna.ie — used to resolve relative /pay/… links */
  payFrontendOrigin: string;
};

export function HotboxChatThread({
  messages,
  error,
  onDismissError,
  showTypingRow,
  scrollAnchorRef,
  emptyPlaceholder = "Send a message to start chatting with the agent.",
  payFrontendOrigin,
}: HotboxChatThreadProps) {
  return (
    <>
      {error ? (
        <Card className="mb-4 border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40">
          <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-red-800 dark:text-red-200">{error.message}</p>
            {onDismissError ? (
              <button
                type="button"
                onClick={onDismissError}
                className="inline-flex h-9 shrink-0 items-center justify-center rounded-md border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-900 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50 dark:hover:bg-neutral-800"
              >
                Dismiss
              </button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="mx-auto flex max-w-2xl flex-col gap-3 pb-4">
        {messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
            {emptyPlaceholder}
          </p>
        ) : null}

        {messages.map((message) => {
          const isUser = message.role === "user";

          if (isUser) {
            const body = textFromMessage(message);
            return (
              <div key={message.id} className="flex w-full justify-end">
                <Card className="max-w-[85%] border-neutral-200 bg-neutral-900 text-neutral-50 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                  <CardContent className="px-4 py-3">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-50">{body}</p>
                  </CardContent>
                </Card>
              </div>
            );
          }

          if (message.role !== "assistant") {
            return null;
          }

          return (
            <div key={message.id} className="flex w-full flex-col items-start gap-2">
              {message.parts.map((part, idx) => {
                if (part.type === "text") {
                  const t = part.text ?? "";
                  if (!t.trim() && part.state !== "streaming") {
                    return null;
                  }
                  return (
                    <div key={`${message.id}-text-${idx}`} className="flex w-full justify-start">
                      <Card className="max-w-[85%] border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
                        <CardContent className="px-4 py-3">
                          <AssistantText
                            text={t}
                            payFrontendOrigin={payFrontendOrigin}
                            isStreaming={part.state === "streaming"}
                          />
                        </CardContent>
                      </Card>
                    </div>
                  );
                }

                if (isToolUIPart(part)) {
                  return (
                    <div key={part.toolCallId} className="flex w-full justify-start">
                      <ToolCallCard part={part} />
                    </div>
                  );
                }

                return null;
              })}
            </div>
          );
        })}

        {showTypingRow ? (
          <div className="flex justify-start">
            <Card className="border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
              <CardContent className="flex items-center gap-2 px-4 py-3">
                <Loader2
                  className="size-4 shrink-0 animate-spin text-neutral-500"
                  aria-hidden
                />
                <span className="text-sm text-neutral-600 dark:text-neutral-300">
                  Assistant is thinking…
                </span>
              </CardContent>
            </Card>
          </div>
        ) : null}

        <div ref={scrollAnchorRef as LegacyRef<HTMLDivElement>} />
      </div>
    </>
  );
}
